// Specialist dashboards — narrowing the board to ONE channel. PURE + tested.
//
// ⚠️⚠️ THE NARROWING HAPPENS AT THE EVALUATION *INPUT*, NEVER IN THE VIEW, and
// that is the whole design of this file. `evaluateAlerts` loops
// `['meta','google']` internally and returns a FLAT list, so filtering its
// OUTPUT would leave a Google account-channel reachable on the Meta page
// through any code path that forgot — the status popover, the verdict counts,
// a deep link, a panel added next year. Narrow the input and the other
// channel never enters the page's state at all, so there is nothing for a
// later component to forget.
//
// ⚠️ `evaluateAlerts` IS REUSED VERBATIM. It gains no channel parameter, and it
// must not: the alert rules have been corrected twice in the last fortnight,
// and a second entry point is a second place for the third correction to be
// missed. What this does is take the SAME function and hand it a book in which
// the other channel is structurally inactive — `channelActive` already returns
// false for an account whose `runs*` flag is off, so the existing gate does the
// work and there is no new rule to keep in step.

import type { AdChannel, HomeAccount, SpineDay } from '../../types/advertisingHome.js';

/** The other one. Written once so no caller hand-rolls the inverse. */
export function otherChannel(channel: AdChannel): AdChannel {
  return channel === 'meta' ? 'google' : 'meta';
}

/** Does this account run the channel at all? The specialist's book membership. */
export function accountRunsChannel(account: HomeAccount, channel: AdChannel): boolean {
  return channel === 'meta' ? account.runsMeta : account.runsGoogle;
}

/**
 * The specialist's book: accounts that run THIS channel, with the other one
 * switched off on each.
 *
 * ⚠️ TWO NARROWINGS, AND BOTH ARE NEEDED.
 *
 *  1. DROP the accounts that do not run the channel. A Google-only club has no
 *     place on the Meta board — it is not the Meta team's to triage, and
 *     leaving it in would put permanently-quiet rows in a list whose whole job
 *     is to be short enough to read.
 *  2. FLIP the surviving accounts' OTHER `runs*` flag to false. This is what
 *     makes it structural rather than conventional: `channelActive` is the gate
 *     `evaluateAlerts` already applies, so with the flag off the other channel
 *     cannot produce an alert, cannot contribute to `healthByAccount`, and
 *     cannot reach the page by any route. ⚠️ Without (2), an account running
 *     BOTH channels would survive (1) and then emit Google alerts onto the Meta
 *     page — which is the exact bug, arriving through the accounts that are
 *     most common rather than the ones that are rare.
 *
 * ⚠️ The BUDGETS are deliberately left alone. `budgetCentsFor` is only read
 * behind `channelActive`, so the other channel's budget is unreachable for
 * alerting — and the book table still renders it, which is information a
 * specialist can legitimately want ("this club spends four times as much on
 * the other channel"). Nulling it would remove that for no safety gain.
 */
export function scopeAccountsToChannel(
  accounts: readonly HomeAccount[],
  channel: AdChannel,
): HomeAccount[] {
  const off = otherChannel(channel);
  return accounts
    .filter((a) => accountRunsChannel(a, channel))
    .map((a) => (off === 'meta' ? { ...a, runsMeta: false } : { ...a, runsGoogle: false }));
}

/**
 * The spine, narrowed to one channel.
 *
 * ⚠️ Belt-and-braces rather than redundant. `scopeAccountsToChannel` already
 * makes the other channel unevaluable, but the spine feeds MORE than the alert
 * core — the pulse windows, the book table's totals, wins & worries, the
 * dominant-currency pick. Every one of those loops rows directly, and none of
 * them consults `runsMeta`. So the rows have to be narrowed too, or a Meta
 * page's pulse would quietly include Google spend.
 */
export function scopeSpineToChannel(
  rows: readonly SpineDay[],
  channel: AdChannel,
): SpineDay[] {
  return rows.filter((r) => r.channel === channel);
}

/**
 * The as-of pair, narrowed: the other channel reads `null`.
 *
 * ⚠️ `null` rather than "leave it" because the freshness line on a
 * single-channel page must not quote a date for a platform it is not showing.
 * Stating "Google data to 2026-09-16" on the Meta board invites the reader to
 * believe the figures beneath it cover both.
 */
export function scopeAsOfToChannel(
  asOf: { meta: string | null; google: string | null },
  channel: AdChannel,
): { meta: string | null; google: string | null } {
  return channel === 'meta'
    ? { meta: asOf.meta, google: null }
    : { meta: null, google: asOf.google };
}
